import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TuiButton, TuiIcon, TuiScrollbar } from '@taiga-ui/core';
import {
  WORKFLOW_FORM_FIELD_INPUT_TYPES,
  WorkflowFormFieldInputPhotos,
  WorkflowFormSubmission,
  WorkflowFormSubmissionExternal,
  WorkflowNodeFormFieldGroup,
  WorkflowNodeFormWithPopulatedFieldsExternal,
} from '@workflows/types';
import {
  InputActualAddressComponent,
  InputButtonFieldComponent,
  InputCheckboxFieldComponent,
  InputDateFieldComponent,
  InputDatetimeFieldComponent,
  InputDealDetailsComponent,
  InputDevicesComponent,
  InputFilesFieldComponent,
  InputMultiselectFieldComponent,
  InputNumberFieldComponent,
  InputMistakesFieldComponent,
  InputPhotosFieldComponent,
  InputPhotosPreviewComponent,
  InputRadioFieldComponent,
  InputSelectFieldComponent,
  InputStringFieldComponent,
  OutputFilesFieldComponent,
  OutputHintFieldComponent,
  OutputLinkFieldComponent,
  OutputRequestCommentsComponent,
  OutputRequestFieldComponent,
  OutputRequestFileCatsComponent,
  OutputTextFieldComponent,
} from '../form-fields';
import { RequestModel } from '@core/models';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  checkSelectedWorkflowFiles,
  getWorkflowControlFiles,
  getPhotoQueueTargetKey,
  getWorkflowFileConstraints,
  getWorkflowFileOrigin,
  type PhotoQueueTarget,
  type WorkflowBackgroundSyncTask,
  type WorkflowFileFieldChange,
  type WorkflowFileRejection,
  type WorkflowFormDraft,
  WorkflowFormSyncCoordinatorService,
  WorkflowService,
} from '@core/services/workflow';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { WorkflowsTranslations } from '@core/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getDealDetailsErrorMessage,
  RequestDealDetailsService,
} from '@core/services/request/request-deal-details.service';
import { XAlertService } from '@core/services/x-alert.service';
import { DeviceDetectorService } from '@core/services/device-detector/device-detector.service';

import { InputPhotosService } from '@core/services/workflow/input-photos.service';
import { FileItem } from '@core/services/workflow/input-photos.types';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime } from 'rxjs/operators';
import {
  buildChangedSubmissionData,
  buildFileFieldsDiff,
} from './workflow-node-form-sync.utils';
import { FormChangeTrackerDirective } from '@core/directives';

@Component({
  selector: 'x-workflow-node-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TuiButton,
    TuiIcon,
    OutputTextFieldComponent,
    OutputRequestFieldComponent,
    OutputRequestFileCatsComponent,
    OutputHintFieldComponent,
    OutputRequestCommentsComponent,
    OutputFilesFieldComponent,
    OutputLinkFieldComponent,
    InputButtonFieldComponent,
    InputNumberFieldComponent,
    InputMistakesFieldComponent,
    InputCheckboxFieldComponent,
    InputDateFieldComponent,
    InputDatetimeFieldComponent,
    InputRadioFieldComponent,
    InputSelectFieldComponent,
    InputMultiselectFieldComponent,
    InputPhotosFieldComponent,
    InputPhotosPreviewComponent,
    InputFilesFieldComponent,
    InputActualAddressComponent,
    InputDealDetailsComponent,
    TuiScrollbar,
    InputStringFieldComponent,
    TranslatePipe,
    InputDevicesComponent,
    FormChangeTrackerDirective,
  ],
  providers: [WorkflowFormSyncCoordinatorService],
  templateUrl: './workflow-node-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'workflow-node',
  },
})
export class WorkflowNodeFormComponent implements OnDestroy {
  public formData =
    input.required<WorkflowNodeFormWithPopulatedFieldsExternal>();
  public $formSubmission = input<WorkflowFormSubmissionExternal | null>(null, {
    alias: 'formSubmission',
  });
  public $workflowIsCompleted = input<boolean>(false, {
    alias: 'workflowIsCompleted',
  });
  public request = input<RequestModel | null>(null);
  public workflowId = input.required<string>();
  public nodeId = input.required<number>();
  public syncData = output<void>();

  private readonly workflowService = inject(WorkflowService);
  private readonly syncCoordinator = inject(WorkflowFormSyncCoordinatorService);
  private readonly dealDetailsService = inject(RequestDealDetailsService);
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly inputPhotosService = inject(InputPhotosService);
  readonly deviceService = inject(DeviceDetectorService);

  /**
   * Track last built formId to prevent duplicate builds
   * Необходимо для предотвращения двойного вызова buildForm при инициализации компонента
   */
  private lastBuiltFormId: number | null = null;

  public readonly mainGroups: Signal<WorkflowNodeFormFieldGroup[]> = computed(
    () =>
      this.formData()?.schema.groups.filter(
        (group: WorkflowNodeFormFieldGroup) =>
          !group.section || group.section === 'main',
      ) ?? [],
  );

  public readonly footerGroups: Signal<WorkflowNodeFormFieldGroup[]> = computed(
    () =>
      this.formData()?.schema.groups.filter(
        (group: WorkflowNodeFormFieldGroup) => group.section === 'footer',
      ) ?? [],
  );

  public readonly inputPhoto: Signal<WorkflowFormFieldInputPhotos | null> =
    computed(() => {
      const groups = this.formData()?.schema.groups || [];

      for (const group of groups) {
        for (const field of group.fields) {
          if (field.type === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputPhotos) {
            return field as WorkflowFormFieldInputPhotos;
          }
        }
      }

      return null;
    });

  /** Поля фото формы: ключ экземпляра поля (заявка, регламент, нода, форма, поле) */
  public readonly $photoTargets: Signal<Record<string, PhotoQueueTarget>> =
    computed(() => {
      const requestNumber = this.request()?.requestNumber;
      const data = this.formData();
      const targets: Record<string, PhotoQueueTarget> = {};

      if (!requestNumber || !data) {
        return targets;
      }

      for (const group of data.schema.groups) {
        for (const field of group.fields) {
          if (field.type === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputPhotos) {
            targets[field.key] = {
              requestNumber,
              workflowId: this.workflowId(),
              nodeId: this.nodeId(),
              formId: data.id,
              fieldId: field.key,
            };
          }
        }
      }

      return targets;
    });

  /** Ключ поля фото, превью которого открывается из этой формы */
  protected readonly $photoTargetKey = computed(() => {
    const photoField = this.inputPhoto();
    const target = photoField ? this.$photoTargets()[photoField.key] : null;

    return target ? getPhotoQueueTargetKey(target) : null;
  });

  protected photoPreviewKey = this.inputPhotosService.photoPreviewKey;

  /** Файлы полей inputFiles при загрузке формы: база для загрузок/удалений при сохранении */
  private loadedFiles: Record<string, readonly File[]> = {};
  /** Файлы, которые не удалось сохранить, с причиной: показываются в поле */
  public readonly $fileFailures = signal<ReadonlyMap<File, string>>(new Map());

  // Form group for reactive forms
  public readonly $workflowForm = signal<FormGroup>(new FormGroup({}));
  public readonly $isSubmitting = signal(false);
  public readonly $canExecuteWorkflow = computed(() =>
    this.workflowService.canExecuteWorkflow(this.workflowId()),
  );
  public readonly $canSetRequestMistakes = computed(() =>
    this.workflowService.canSetRequestMistakes(this.workflowId()),
  );

  // Флаг для отслеживания, восстановлен ли драфт
  private draftRestored = false;
  /** Поколение сборки формы: поздние асинхронные результаты прошлой формы/заявки не применяются */
  private formBuildGeneration = 0;
  /** Ошибка сохранения деталей сделки: показывается до успешного повторного сохранения */
  readonly $dealDetailsSaveError = signal<string | null>(null);
  private draftSaveFailureNotified = false;

  constructor() {
    // Build form when formData changes
    effect(() => {
      const data = this.formData();
      const submission = this.$formSubmission();

      if (data) {
        // Prevent duplicate builds for the same formId
        if (this.lastBuiltFormId === data.id) {
          return;
        }

        this.lastBuiltFormId = data.id;
        // Call async buildForm without awaiting - form will update when files are loaded
        void this.buildForm(data, submission);
      }
    });

    effect(() => {
      if (this.$photoTargetKey() !== this.photoPreviewKey()) {
        this.photoPreviewKey.set(null);
      }
    });

    // Значение полей фото = фото этого экземпляра поля на сервере и в очереди:
    // так к ним применяются required/min/max/MIME валидаторы формы
    effect(() => {
      const form = this.$workflowForm();
      const targets = this.$photoTargets();

      for (const [fieldKey, target] of Object.entries(targets)) {
        form
          .get(fieldKey)
          ?.setValue(this.inputPhotosService.getTargetValue(target), {
            emitEvent: false,
          });
      }
    });

    // Подписываемся на изменения формы ОДИН РАЗ при создании компонента
    // Effect только сигнализирует о смене формы, но не создает новые подписки
    effect(() => {
      const form = this.$workflowForm();

      // Если форма изменилась (новая форма была создана)
      if (form && !this.$workflowIsCompleted()) {
        form.valueChanges
          .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            // Сохраняем драфт только если:
            // 1. Форма уже была инициализирована (draftRestored)
            // 2. Форма реально изменялась (dirty)
            if (this.draftRestored && form.dirty) {
              void this.saveDraft();
            }
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.saveDraft().catch(error => {
      console.error('Failed to save draft on destroy:', error);
    });
  }

  /**
   * Сохранить текущее состояние формы как драфт
   */
  private async saveDraft(): Promise<void> {
    if (this.$workflowIsCompleted()) {
      // Не сохраняем драфт для завершенных workflow
      return;
    }
    const req = this.request();
    const data = this.formData();
    const form = this.$workflowForm();

    if (!req || !data || !form.dirty) {
      // Не сохраняем если форма не изменялась
      return;
    }

    try {
      await this.workflowService.saveFormDraft(
        req.requestNumber,
        this.workflowId(),
        this.nodeId(),
        data.id,
        this.getDraftValue(form.value, data),
      );
      this.draftSaveFailureNotified = false;
    } catch (error) {
      console.error('Failed to save form draft:', error);

      // Не обещаем сохранение, которого нет; предупреждаем один раз до следующей успешной записи
      if (!this.draftSaveFailureNotified) {
        this.draftSaveFailureNotified = true;
        this.xAlertService.error(
          this.outkeepTranslateService.instant(
            WorkflowsTranslations.DraftNotSavedLocally,
          ),
        );
      }
    }
  }

  /**
   * Build reactive form based on schema
   */
  private async buildForm(
    data: WorkflowNodeFormWithPopulatedFieldsExternal,
    submission: WorkflowFormSubmissionExternal | null,
  ): Promise<void> {
    const generation = ++this.formBuildGeneration;
    const formGroup = new FormGroup({});

    this.loadedFiles = {};
    this.$fileFailures.set(new Map());
    const canExecuteWorkflow = this.$canExecuteWorkflow();
    const isReadonly = this.$workflowIsCompleted() || !canExecuteWorkflow;
    const req = this.request();

    // Проверяем, есть ли сохраненный драфт
    let draft: WorkflowFormDraft | null = null;

    if (req && !submission) {
      // Загружаем драфт только если нет submission (форма еще не сабмичена)
      const draftResult = await this.workflowService.getFormDraft(
        req.requestNumber,
        this.workflowId(),
        this.nodeId(),
        data.id,
      );

      if (generation !== this.formBuildGeneration) {
        return;
      }

      if (draftResult.status === 'restored') {
        draft = draftResult.draft;
      } else if (draftResult.status === 'discarded') {
        this.xAlertService.warn(
          this.outkeepTranslateService.instant(
            WorkflowsTranslations.DraftDiscardedVersionChanged,
          ),
        );
      }
    }

    // Проверяем, есть ли в submission хотя бы одно заполненное значение
    const hasAnySubmissionValue =
      submission?.data &&
      Object.values(submission.data).some(
        field => field?.value !== undefined && field?.value !== null,
      );

    // First pass: create all controls with default values
    data.schema.groups.forEach(group => {
      group.fields.forEach(field => {
        // Only create controls for input fields
        if (this.isInputField(field.type)) {
          const validation =
            'validation' in field ? field.validation : undefined;
          const validators = this.workflowService.buildValidators(
            validation,
            field.type, // Pass field type to buildValidators
          );

          // Get value from submission if exists
          const submissionValue = submission?.data?.[field.key]?.value;
          // Get value from draft if exists
          const draftValue = draft?.formValue?.[field.key];

          // For file fields, start with null, will be populated async
          // For date/datetime fields, transformer handles conversion automatically
          // For other fields, priority: draft > submission > default
          let defaultValue;

          if (
            field.type === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputFiles ||
            field.type === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputPhotos
          ) {
            defaultValue = null;
          } else {
            // Приоритет: драфт > submission > дефолт
            defaultValue =
              draftValue ?? submissionValue ?? this.getDefaultValue(field);
          }

          const control = new FormControl(defaultValue, validators);

          // Это позволит пользователю увидеть все ошибки валидации сразу в форме
          if (hasAnySubmissionValue) {
            control.markAsTouched();
          }

          if (isReadonly) {
            control.disable();
          }

          formGroup.addControl(field.key, control);
        }
      });
    });

    // Set form group immediately so form is usable
    this.$workflowForm.set(formGroup);

    // Устанавливаем флаг что драфт восстановлен
    // Это нужно чтобы не сохранять драфт при первых изменениях после восстановления
    this.draftRestored = true;

    // Second pass: файлы полей inputFiles (сервер + очередь на устройстве).
    // Фото заполняются отдельно из InputPhotosService (см. effect в конструкторе)
    for (const group of data.schema.groups) {
      for (const field of group.fields) {
        if (field.type !== WORKFLOW_FORM_FIELD_INPUT_TYPES.inputFiles || !req) {
          continue;
        }

        const files = await this.workflowService.loadFileFieldValue(
          {
            requestNumber: req.requestNumber,
            workflowId: this.workflowId(),
            nodeId: this.nodeId(),
            formId: data.id,
          },
          field.key,
          submission,
        );

        // Пока шли HEAD-запросы, форма или заявка могли смениться
        if (generation !== this.formBuildGeneration) {
          return;
        }

        this.loadedFiles[field.key] = files;

        const control = formGroup.get(field.key);
        // Файлы, выбранные пользователем во время загрузки, сохраняются
        const pickedFiles = getWorkflowControlFiles(control?.value).filter(
          file => !getWorkflowFileOrigin(file),
        );
        const value = [...files, ...pickedFiles];

        if (control && value.length > 0) {
          control.setValue(value);
        }
      }
    }

    if (generation !== this.formBuildGeneration) {
      return;
    }

    // Third pass: check for saved validation errors in IndexedDB
    await this.restoreSavedValidationErrors();
  }

  /**
   * Check if field is an input field
   */
  private isInputField(type: string): boolean {
    return Object.values(WORKFLOW_FORM_FIELD_INPUT_TYPES).includes(type as any);
  }

  /**
   * Get default value for field
   */
  private getDefaultValue(field: any): any {
    if (field.defaultValue !== undefined) {
      return field.defaultValue;
    }

    // Return appropriate default based on type
    switch (field.type) {
      case WORKFLOW_FORM_FIELD_INPUT_TYPES.inputCheckbox:
        return false;
      case WORKFLOW_FORM_FIELD_INPUT_TYPES.inputNumber:
        return null;
      case WORKFLOW_FORM_FIELD_INPUT_TYPES.inputMultiselect:
      case WORKFLOW_FORM_FIELD_INPUT_TYPES.inputFiles:
      case WORKFLOW_FORM_FIELD_INPUT_TYPES.inputPhotos:
        return [];
      default:
        return null;
    }
  }

  /**
   * Get form control for a field
   */
  public getControl(fieldKey: string): FormControl {
    return this.$workflowForm().get(fieldKey) as FormControl;
  }

  /**
   * Submit form
   */
  public async sync() {
    if (this.$isSubmitting()) {
      return;
    }

    const form = this.$workflowForm();
    const data = this.formData();
    const req = this.request()!;

    if (this.$workflowIsCompleted() || !this.$canExecuteWorkflow()) {
      this.syncData.emit();

      return;
    }

    try {
      this.$isSubmitting.set(true);

      // Execute all registered before-sync handlers
      await this.syncCoordinator.executeBeforeSyncHandlers();
      // Очищаем хендлеры после успешного выполнения
      this.syncCoordinator.clearAllHandlers();
      // Mark all fields as touched to show validation errors
      Object.keys(form.controls).forEach(key =>
        form.controls[key].markAsTouched(),
      );

      if (!data || !req) {
        console.error('Missing form data or request');

        return;
      }

      const existingSubmission =
        this.$formSubmission() ??
        this.workflowService.getSubmission(this.workflowId(), data.id);
      const syncPreparation = buildChangedSubmissionData({
        schema: data.schema,
        formValue: form.value as Record<string, unknown>,
        existingSubmissionData: existingSubmission?.data,
      });

      if (syncPreparation.invalidNumberFieldKey) {
        // Уведомляем пользователя сразу, чтобы не отправлять некорректное число на backend.
        this.xAlertService.error(
          `Поле "${syncPreparation.invalidNumberFieldKey}": значение должно быть числом`,
        );

        return;
      }

      const formData = syncPreparation.changedData;

      const nextFormId = this.workflowService.getNextFormInWorkflow(
        this.workflowId(),
        this.nodeId(),
        data.id,
      );

      const hasChangedSubmissionData = Object.keys(formData).length > 0;
      const submissionPayload: {
        data?: WorkflowFormSubmission['data'];
      } = {};

      if (hasChangedSubmissionData) {
        submissionPayload.data = formData;
      }

      const payload = {
        submission: submissionPayload,
        ...(nextFormId && { nextFormId }),
      };

      const backgroundTask: WorkflowBackgroundSyncTask = {
        requestNumber: req.requestNumber,
        workflowId: this.workflowId(),
        nodeId: this.nodeId(),
        formId: data.id,
        body: payload,
        isFormValid: form.valid,
      };

      // Детали сделки сохраняются до перехода дальше: при ошибке мастер остаётся на форме
      // и показывает постоянный статус «не сохранено» (повтор — повторное сохранение формы)
      const syncDealDetails = this.buildDealDetailsSyncHandler();

      if (syncDealDetails) {
        try {
          await syncDealDetails();
          this.$dealDetailsSaveError.set(null);
        } catch (error) {
          this.$dealDetailsSaveError.set(
            getDealDetailsErrorMessage(error) ||
              this.outkeepTranslateService.instant(
                WorkflowsTranslations.DealDetailsNotSaved,
              ),
          );

          return;
        }
      }

      // Файлы полей загружаются/удаляются до сохранения формы: при ошибке мастер
      // остаётся на форме, а поле показывает, какие файлы не сохранены
      const fileChanges = buildFileFieldsDiff({
        schema: data.schema,
        formValue: form.value as Record<string, unknown>,
        loadedFiles: this.loadedFiles,
      });

      if (
        fileChanges.length > 0 &&
        !(await this.syncFileFields(req.requestNumber, data.id, fileChanges))
      ) {
        return;
      }

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        // Без сети: сначала записываем задачу на устройство, и только потом уходим с формы
        try {
          await this.workflowService.queueFormOffline(backgroundTask);
        } catch (error) {
          console.error('Failed to queue offline form submission:', error);
          this.xAlertService.error(
            this.outkeepTranslateService.instant(
              WorkflowsTranslations.OfflineFormNotSaved,
            ),
          );

          return;
        }

        this.xAlertService.warn(
          this.outkeepTranslateService.instant(
            WorkflowsTranslations.OfflineFormQueued,
          ),
        );
        this.syncData.emit();

        return;
      }

      this.workflowService.syncFormInBackground(backgroundTask);
      this.syncData.emit();
    } catch (error) {
      console.error('Before-sync handlers failed:', error);
      // Общее уведомление о сбое на этапе подготовки данных перед sync.
      this.xAlertService.error('Ошибка при подготовке данных');
    } finally {
      this.$isSubmitting.set(false);
    }
  }

  /**
   * Сохранить изменения файлов полей. false — часть файлов не сохранена, форму не отправляем.
   */
  private async syncFileFields(
    requestNumber: string,
    formId: number,
    changes: WorkflowFileFieldChange[],
  ): Promise<boolean> {
    const result = await this.workflowService.syncFileFieldChanges(
      {
        requestNumber,
        workflowId: this.workflowId(),
        nodeId: this.nodeId(),
        formId,
      },
      changes,
    );
    const failedFiles = new Set(result.failures.map(failure => failure.file));
    const form = this.$workflowForm();

    for (const change of changes) {
      const control = form.get(change.fieldKey);
      // Файлы, которые не удалось удалить на сервере, возвращаются в поле
      const notRemoved = change.remove.filter(file => failedFiles.has(file));
      const value = [...getWorkflowControlFiles(control?.value), ...notRemoved];

      if (control && notRemoved.length > 0) {
        control.setValue(value);
      }

      this.loadedFiles[change.fieldKey] = value.filter(file =>
        getWorkflowFileOrigin(file),
      );
    }

    this.$fileFailures.set(
      new Map(result.failures.map(failure => [failure.file, failure.message])),
    );

    if (result.failures.length > 0) {
      this.xAlertService.error(
        this.outkeepTranslateService.instant(
          WorkflowsTranslations.FilesNotSaved,
        ),
      );

      return false;
    }

    if (result.queued > 0) {
      this.xAlertService.warn(
        this.outkeepTranslateService.instant(WorkflowsTranslations.FilesQueued),
      );
    }

    return true;
  }

  /** Значение формы для черновика: фото хранятся в очереди фото, не в черновике */
  private getDraftValue(
    formValue: Record<string, unknown>,
    data: WorkflowNodeFormWithPopulatedFieldsExternal,
  ): Record<string, unknown> {
    const draftValue = { ...formValue };

    for (const group of data.schema.groups) {
      for (const field of group.fields) {
        if (field.type === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputPhotos) {
          delete draftValue[field.key];
        }
      }
    }

    return draftValue;
  }

  private buildDealDetailsSyncHandler(): (() => Promise<void>) | undefined {
    const dealDetailsForm = this.dealDetailsService.currentDealDetailsForm;

    if (!dealDetailsForm || !this.formContainsDealDetails()) {
      return undefined;
    }

    return async () => {
      await firstValueFrom(
        this.dealDetailsService.updateRequestDealDetails$(
          dealDetailsForm,
          false,
          true,
        ),
      );
      this.dealDetailsService.currentDealDetailsForm = null;
    };
  }

  private formContainsDealDetails(): boolean {
    const data = this.formData();

    if (!data) return false;

    for (const group of data.schema.groups) {
      for (const field of group.fields) {
        if (
          field.type === WORKFLOW_FORM_FIELD_INPUT_TYPES.inputDealDetails &&
          !!this.dealDetailsService.currentDealDetailsForm
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Восстановить сохранённые ошибки валидации из IndexedDB
   */
  private async restoreSavedValidationErrors(): Promise<void> {
    const req = this.request();

    if (!req) return;

    const savedErrors = await this.workflowService.getFormErrors(
      req.requestNumber,
      this.workflowId(),
      this.nodeId(),
      this.formData().id,
    );

    if (savedErrors && savedErrors.errors.length > 0) {
      // Применяем ошибки к контролам формы через фасад
      this.workflowService.applyErrorsToForm(
        savedErrors.errors,
        this.$workflowForm().controls,
      );

      // Показываем уведомление пользователю
      this.xAlertService.warn('В форме обнаружены ошибки!');
    }
  }

  protected onPhotoSelected(files: File[]) {
    const photoField = this.inputPhoto();
    const target = photoField ? this.$photoTargets()[photoField.key] : null;

    if (!photoField || !target) return;

    if (files.length > 0) {
      const check = checkSelectedWorkflowFiles(
        files,
        getWorkflowFileConstraints(photoField, 'image/*'),
        this.inputPhotosService.getTargetValue(target).length,
      );

      this.getControl(photoField.key)?.markAsTouched();

      if (check.rejected.length > 0) {
        this.showRejectedFiles(check.rejected, photoField);
      }

      files = check.accepted;

      if (files.length === 0) {
        return;
      }
    }

    const fileItems: FileItem[] = files.map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      status: 'new',
      settings: {
        requestNumber: this.request()?.requestNumber || '',
        workflowId: this.workflowId(),
        nodeId: this.nodeId(),
        formId: this.formData()?.id,
        fieldId: photoField.key,
      },
    }));

    this.inputPhotosService.addFiles(fileItems);
    this.photoPreviewKey.set(getPhotoQueueTargetKey(target));
  }

  private showRejectedFiles(
    rejected: WorkflowFileRejection[],
    field: WorkflowFormFieldInputPhotos,
  ): void {
    const constraints = getWorkflowFileConstraints(field, 'image/*');
    const messages = rejected.map(({ file, reason }) => {
      const reasonText =
        reason === 'mimetype'
          ? this.outkeepTranslateService.instant(
              WorkflowsTranslations.FileInvalidType,
            )
          : reason === 'size'
            ? this.outkeepTranslateService.instant(
                WorkflowsTranslations.FileTooLarge,
                { max: Math.round(constraints.maxFileSizeBytes / 1_000_000) },
              )
            : this.outkeepTranslateService.instant(
                WorkflowsTranslations.FileTooMany,
                { max: constraints.maxFilesCount ?? 0 },
              );

      return this.outkeepTranslateService.instant(
        WorkflowsTranslations.FileRejected,
        { name: file.name, reason: reasonText },
      );
    });

    this.xAlertService.error(messages.join('\n'));
  }

  protected photoSync() {
    this.photoPreviewKey.set(null);
  }
}
